import React, { useState } from 'react';
import { Input, Button, Tooltip, Space } from 'antd';
import { PictureOutlined } from '@ant-design/icons';
import FilesSelector from './FilesSelector';
import { CommonProps, defaultRtn } from '../../../utilsTs/declareTypes';
import { InsertFileOptions } from './BaseFiles';

interface FilesInputProps extends Pick<CommonProps, 'defaultService'> {
  /** 输入框当前值 */
  value?: string;
  /** 值变化回调 */
  onChange?: (value: string) => void;
  /** 占位文本 */
  placeholder?: string;
  /** 容器样式，可覆盖默认宽度100% */
  style?: React.CSSProperties;
  /** 触发按钮的图标，默认为 PictureOutlined */
  buttonIcon?: React.ReactNode;
  /** 按钮的 Tooltip 提示文字 */
  buttonTooltip?: string;
  /** 透传给内部 Input 的其他属性 */
  inputProps?: React.ComponentProps<typeof Input>;
}

/**
 * FilesInput 组件
 * 用于在输入框中展示文件路径，通过点击右侧图标打开文件选择器（FilesSelector）来选取文件，
 * 并将选中的文件完整路径自动回填到输入框中。
 */
const FilesInput: React.FC<FilesInputProps> = ({
  defaultService,
  value,
  onChange,
  placeholder = '请输入',
  style,
  buttonIcon = <PictureOutlined />,
  buttonTooltip = '选择图片',
  inputProps,
}) => {
  /** 控制文件选择弹窗的显隐 */
  const [modalVisible, setModalVisible] = useState(false);

  /** 输入变化处理 */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  };

  /** 打开文件选择弹窗 */
  const openFilesSelector = () => {
    setModalVisible(true);
  };

  /** 关闭文件选择弹窗 */
  const handleCancel = () => {
    setModalVisible(false);
  };

  return (
    <>
      {/* 默认宽度 100% 实现自适应，外部传入 style 可覆盖（如固定宽度） */}
      <Space.Compact
        style={{
          width: '100%',   // 默认占满父容器
          ...style,        // 外部样式可覆盖，例如 { width: 300 } 即可固定
        }}
      >
        <Input
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          {...inputProps} // 允许外部覆盖或扩展
        />
        <Tooltip title={buttonTooltip}>
          <Button
            type="primary"
            shape="circle"
            icon={buttonIcon}
            onClick={openFilesSelector}
          />
        </Tooltip>
      </Space.Compact>

      {/* 文件选择弹窗 */}
      <FilesSelector
        titleName="单选图片"
        selectMode='single'
        defaultService={defaultService}
        visible={modalVisible}
        closeModal={handleCancel}
        insertFile={async (payload: InsertFileOptions) => {
          const { fileRows } = payload;
          if (fileRows.length > 0) {
            /** 拼接文件的完整访问路径，例如：https://xxx.com/uploads/abc.jpg */
            const fileValue = `${fileRows[0].serverUrl}/${fileRows[0].destination}/${fileRows[0].fileName}`;
            // 更新输入框的值
            onChange?.(fileValue);
            // 关闭弹窗
            setModalVisible(false);
            return defaultRtn({ success: true });
          } else {
            return defaultRtn({ success: false });
          }
        }}
      />
    </>
  );
};

export default FilesInput;